/**
 * V6-010 砺锋台 V2 — Mock 关卡池 + 匹配 + Mock 战斗 + 个人记录
 *
 * P0 阶段：mock 数据 + 随机胜负 + 段位修正 + PB 追踪
 * P1 阶段：替换为真实 Pyodide 战斗引擎
 *
 * @see design/v6-sharpen/gdd-v6-010-sharpen.md §6 §7 §8 §21.1 §21.2
 * @date 2026-07-19
 * @updated 2026-07-20 — 提案 1/2：段位匹配 + PB 追踪
 */

import type { MockLevel, OpenType, TypeProgress, RankId, PersonalBest, LevelEntry } from "./types";
import { getRankForAiLevel, RANK_CONFIGS, difficultyToAiLevel, difficultyToRank } from "./types";
import { rankAdjustedWinRate } from "./level-system";

// ============================================================================
// Mock 关卡池（复用 demo 10 种 ai_type）
// ============================================================================

const DEMO_AI_POOL: Array<{ aiType: string; aiLevel: number; name: string; description: string }> = [
  { aiType: "passive",      aiLevel: 1,  name: "初战", description: "敌人几乎不动，射击距离内才开火" },
  { aiType: "cover_seeker", aiLevel: 2,  name: "石壁", description: "频繁寻找掩体，低血量时躲得更快" },
  { aiType: "scanner",      aiLevel: 3,  name: "耳目", description: "主动扫描，视野极好" },
  { aiType: "balanced",     aiLevel: 4,  name: "均势", description: "攻守兼备，根据形势切换" },
  { aiType: "flanker",      aiLevel: 5,  name: "侧袭", description: "迂回包抄，不走直线" },
  { aiType: "defensive",    aiLevel: 6,  name: "铁壁", description: "固守型，长期占据掩体" },
  { aiType: "aggressive",   aiLevel: 7,  name: "冲锋", description: "激进冲锋，看见就射击" },
  { aiType: "camper",       aiLevel: 8,  name: "守株", description: "守株待兔，靠近才出手" },
  { aiType: "veteran",      aiLevel: 9,  name: "老将", description: "综合型，掩体+扫描+进攻三位一体" },
  { aiType: "boss",         aiLevel: 10, name: "淬火", description: "终极 Boss，混合所有策略" },
];

/** 每类型独立 mock 关卡池（10 关，带 rank 字段） */
export const MOCK_LEVELS: Record<OpenType, MockLevel[]> = {
  "1v1": DEMO_AI_POOL.map((ai, i) => ({
    id: `c${String(i + 1).padStart(3, "0")}`,
    name: ai.name,
    aiType: ai.aiType,
    aiLevel: ai.aiLevel,
    description: ai.description,
    rank: getRankForAiLevel(ai.aiLevel),
  })),
  "2v2": DEMO_AI_POOL.map((ai, i) => ({
    id: `c${String(i + 1).padStart(3, "0")}-2v2`,
    name: `${ai.name}·双坦`,
    aiType: ai.aiType,
    aiLevel: ai.aiLevel,
    description: `${ai.description}（2v2 模式）`,
    rank: getRankForAiLevel(ai.aiLevel),
  })),
  "3v3": DEMO_AI_POOL.map((ai, i) => ({
    id: `c${String(i + 1).padStart(3, "0")}-3v3`,
    name: `${ai.name}·三坦`,
    aiType: ai.aiType,
    aiLevel: ai.aiLevel,
    description: `${ai.description}（3v3 模式）`,
    rank: getRankForAiLevel(ai.aiLevel),
  })),
};

// ============================================================================
// 关卡匹配（4 段军衔阶梯 + 自适应难度，提案 1）
// ============================================================================

/**
 * 从该类型未通关池中，按段位优先排序取 3 关
 * 规则：同段优先 → 段内最低 ai_level 优先 → 最多跨 1 段
 */
export function pickThreeLevels(
  type: OpenType,
  progress: TypeProgress
): MockLevel[] {
  const allLevels = MOCK_LEVELS[type];
  const pool = allLevels.filter(l => !progress.clearedLevels.includes(l.id));
  if (pool.length === 0) return [];

  // 按 rankIndex 排序，同段内按 aiLevel 排序
  const sorted = pool.sort((a, b) => {
    const ra = RANK_CONFIGS.find(r => r.id === a.rank)!.index;
    const rb = RANK_CONFIGS.find(r => r.id === b.rank)!.index;
    if (ra !== rb) return ra - rb;
    return a.aiLevel - b.aiLevel;
  });

  // 取 3 个，限制不跨超过 1 段
  const result: MockLevel[] = [];
  if (sorted.length === 0) return [];
  const baseRank = RANK_CONFIGS.find(r => r.id === sorted[0].rank)!.index;
  for (const level of sorted) {
    if (result.length >= 3) break;
    const rankIdx = RANK_CONFIGS.find(r => r.id === level.rank)!.index;
    if (rankIdx - baseRank <= 1) result.push(level);
  }
  return result;
}

/** 检查该类型是否已全通关 */
export function isAllCleared(type: OpenType, progress: TypeProgress): boolean {
  const allLevels = MOCK_LEVELS[type];
  return allLevels.every(l => progress.clearedLevels.includes(l.id));
}

// ============================================================================
// Mock 战斗（段位修正胜率，提案 1）
// ============================================================================

/**
 * Mock 单场战斗（段位修正胜率）
 *
 * 同段: 50% | 越1段: 30% | 越2段: 15% | 越3段: 10%
 */
/** Mock 战斗（MockLevel 原始签名，保留向后兼容） */
export function mockBattle(
  level: MockLevel,
  playerRankIndex: number
): { result: "win" | "loss"; hpPct: number; timeMs: number };
/** V6-011: Mock 战斗（LevelEntry，从 difficulty 推导 rank） */
export function mockBattle(
  level: LevelEntry
): { result: "win" | "loss"; hpPct: number; timeMs: number };
export function mockBattle(
  level: MockLevel | LevelEntry,
  playerRankIndex?: number
): { result: "win" | "loss"; hpPct: number; timeMs: number } {
  let levelRankIdx: number;
  let aiLevel: number;

  if ('aiLevel' in level) {
    // MockLevel 路径
    const mock = level as MockLevel;
    levelRankIdx = RANK_CONFIGS.find(r => r.id === mock.rank)!.index;
    aiLevel = mock.aiLevel;
  } else {
    // LevelEntry 路径（V6-011）
    const entry = level as LevelEntry;
    levelRankIdx = RANK_CONFIGS.find(r => r.id === difficultyToRank(entry.difficulty))!.index;
    aiLevel = difficultyToAiLevel(entry.difficulty);
  }

  const playerRankIdx = playerRankIndex ?? 0; // V6-011: 默认 rank 0（战士）
  const winRate = rankAdjustedWinRate(playerRankIdx, levelRankIdx);
  const isWin = Math.random() < winRate;

  const hpPct = isWin
    ? Math.round(30 + Math.random() * 70)  // 赢: 30-100%
    : Math.round(Math.random() * 30);       // 输: 0-30%

  const baseTime = 15_000 + aiLevel * 2_000; // 15-35s baseline
  const timeMs = Math.round(baseTime + (Math.random() - 0.5) * 10_000);

  return { result: isWin ? "win" : "loss", hpPct, timeMs };
}

/** Mock 一轮战斗（批量） */
export function mockBattleRound(
  levels: MockLevel[],
  playerRankIndex: number
): Array<{ level: MockLevel; result: "win" | "loss"; hpPct: number; timeMs: number }> {
  return levels.map(level => {
    const battle = mockBattle(level, playerRankIndex);
    return { level, ...battle };
  });
}

// ============================================================================
// 个人最佳记录判定（提案 2）
// ============================================================================

/** 创建初始 PB */
function createEmptyPB(): PersonalBest {
  return {
    bestHpPct: 0,
    bestTimeMs: Infinity,
    longestStreak: 0,
    firstClearAt: 0,
    clearCount: 0,
  };
}

/** 更新个人最佳 */
function updatePB(
  pb: PersonalBest,
  result: "win" | "loss",
  hpPct: number,
  timeMs: number,
  currentStreak: number,
  now: number
): PersonalBest {
  const updated = { ...pb };
  if (hpPct > pb.bestHpPct) updated.bestHpPct = hpPct;
  if (result === "win" && timeMs < pb.bestTimeMs) updated.bestTimeMs = timeMs;
  if (result === "win") {
    updated.clearCount++;
    if (pb.firstClearAt === 0) updated.firstClearAt = now;
    if (currentStreak > pb.longestStreak) updated.longestStreak = currentStreak;
  }
  return updated;
}

// ============================================================================
// 战斗结果应用 + 段位晋升判定（提案 1+2 核心）
// ============================================================================

export interface ApplyBattlesResult {
  progress: TypeProgress;
  leveledUp: boolean;
  oldLevel: number;
  newLevel: number;
  rankPromoted: boolean;
  promotedFrom: RankId | null;
  promotedTo: RankId | null;
}

import { levelFromExp } from "./level-system";

/**
 * 应用 3 场战斗结果到 progress（含 PB 更新和段位晋升判定）
 */
export function applyBattles(
  progress: TypeProgress,
  battleResults: Array<{
    level: MockLevel;
    result: "win" | "loss";
    hpPct: number;
    timeMs: number;
  }>
): ApplyBattlesResult {
  const oldLevel = levelFromExp(progress.exp);
  const now = Date.now();
  let newExp = progress.exp;
  let currentStreak = progress.personalBests.currentStreak;
  const pbMap = { ...progress.pbMap };
  const rankProgress = { ...progress.rankProgress };
  let rankPromoted = false;
  let promotedFrom: RankId | null = null;
  let promotedTo: RankId | null = null;

  // 逐场处理
  for (const { level, result, hpPct, timeMs } of battleResults) {
    // 经验
    if (result === "win") {
      newExp++;
      currentStreak++;
    } else {
      currentStreak = 0;
    }

    // 个人最佳
    const pb = pbMap[level.id] || createEmptyPB();
    pbMap[level.id] = updatePB(pb, result, hpPct, timeMs, currentStreak, now);

    // 段位进度
    const rp = rankProgress[level.rank];
    if (rp) {
      rp.battles++;
    }
  }

  const newLevel = levelFromExp(newExp);

  // 更新通关列表
  const newCleared = [...progress.clearedLevels];
  for (const { level, result } of battleResults) {
    if (result === "win" && !newCleared.includes(level.id)) {
      newCleared.push(level.id);
      // 段位通关计数
      const rp = rankProgress[level.rank];
      if (rp) rp.cleared++;
    }
  }

  // 段位晋升判定
  const curRankConfig = RANK_CONFIGS.find(r => r.id === progress.currentRank)!;
  const curEntry = rankProgress[progress.currentRank];
  const promotionPct = curEntry.total > 0 ? curEntry.cleared / curEntry.total : 0;

  let newCurrentRank = progress.currentRank;
  if (curRankConfig.index < RANK_CONFIGS.length - 1 && promotionPct >= 0.8 && curEntry.cleared > 0) {
    promotedFrom = progress.currentRank;
    newCurrentRank = RANK_CONFIGS[curRankConfig.index + 1].id;
    promotedTo = newCurrentRank;
    rankPromoted = true;
  }

  const newProgress: TypeProgress = {
    ...progress,
    exp: newExp,
    totalBattles: progress.totalBattles + battleResults.length,
    wins: progress.wins + battleResults.filter(b => b.result === "win").length,
    losses: progress.losses + battleResults.filter(b => b.result === "loss").length,
    clearedLevels: newCleared,
    recentBattles: battleResults.map(b => ({
      levelId: b.level.id,
      levelName: b.level.name,
      aiType: b.level.aiType,
      result: b.result,
      hpPct: b.hpPct,
      timeMs: b.timeMs,
      exp: b.result === "win" ? 1 : 0,
      timestamp: now,
      rank: b.level.rank,
      isNewRecord: false, // screen-10 侧重新判定
      recordBroken: [],
      isRankUp: false,
    })),
    currentRank: newCurrentRank,
    rankProgress,
    pbMap,
    personalBests: {
      longestStreak: Math.max(progress.personalBests.longestStreak, currentStreak),
      currentStreak,
    },
  };

  return {
    progress: newProgress,
    leveledUp: newLevel > oldLevel,
    oldLevel,
    newLevel,
    rankPromoted,
    promotedFrom,
    promotedTo,
  };
}

// ============================================================================
// 初始进度工厂
// ============================================================================

export function createInitialTypeProgress(): TypeProgress {
  const rp: Record<string, { cleared: number; total: number; battles: number }> = {};
  for (const rc of RANK_CONFIGS) {
    rp[rc.id] = { cleared: 0, total: rc.floorCount, battles: 0 };
  }
  return {
    exp: 0,
    totalBattles: 0,
    wins: 0,
    losses: 0,
    clearedLevels: [],
    failedLevels: [],
    recentBattles: [],
    code: "",
    currentRank: "soldier" as RankId,
    rankProgress: rp as TypeProgress["rankProgress"],
    pbMap: {},
    personalBests: { longestStreak: 0, currentStreak: 0 },
  };
}
